/**
 * THE STATUS FILTER ON THE ARTICLES LIST, DRAWN AS THE PROTOTYPE'S PILLS (design 15 - the owner's
 * live look at real cabinets, 2026-10-01; the prototype's `artChips`).
 *
 * STILL A FILTER OF SEVERAL STATES AT ONCE. `listOwnArticles`' `status` takes several values and
 * the list has always let an author pick more than one ("Drafts" and "Changes requested" together),
 * so this is the kit's `PillChoice` in its `multiple` mode (since 2026-10-05; before, this file
 * copied the kit's pill classes because the kit chose exactly one): each pill a button that says
 * whether it is pressed. "All" is pressed while nothing is picked, and pressing it clears the
 * filter: it is the old "Clear" button, where the prototype draws it.
 *
 * NO COUNT ON A PILL. There is no aggregate-counts operation; a count per pill would be seven reads
 * per load (./ArticlesRoute.tsx says the rest).
 */
import { ARTICLE_STATUSES } from '../../features/cabinet/api';
import type { ArticleStatus } from '../../features/cabinet/api';
import { PillChoice, statusLabel } from '../../features/cabinet/components';
import { useTranslation } from '../../i18n';

export interface StatusFilterProps {
  readonly selected: readonly ArticleStatus[];
  readonly onToggle: (status: ArticleStatus) => void;
  readonly onClear: () => void;
}

const PILL_STATUSES = ARTICLE_STATUSES.filter(hasItsOwnPill);

export function StatusFilter({ selected, onToggle, onClear }: StatusFilterProps) {
  const { t } = useTranslation('articles');
  // Named at render, not when the module loads, so a language change renames the pills.
  const options = PILL_STATUSES.map((status) => ({ value: status, label: statusLabel(status) }));
  return (
    <PillChoice
      multiple
      label={t('list.statusFilter.label')}
      options={options}
      value={selected}
      onToggle={(status) => toggleTogether(status, selected, onToggle)}
      clear={{ label: t('list.statusFilter.all'), onClear }}
    />
  );
}

/**
 * ★ "ARCHIVED" ALSO LISTS THE TAKEN-DOWN (design 24, T1: the owner's option 2, 2026-10-11). The
 * prototype has no "Taken down" pill, and its "Archived" means off the site, which an editor's
 * takedown (`unpublished`) is too. So `unpublished` has no pill of its own, and pressing "Archived"
 * turns both statuses on or off together: each one not yet where `archived` is going is toggled, so
 * the two never drift apart. The route sends every selected status in its one read. Each card still
 * says which it is, grey "Archived" or red "Taken down" (status-pill.tsx), and "All" lists both.
 */
const OFF_THE_SITE: readonly ArticleStatus[] = ['archived', 'unpublished'];

function hasItsOwnPill(status: ArticleStatus): boolean {
  return status !== 'unpublished';
}

function toggleTogether(
  status: ArticleStatus,
  selected: readonly ArticleStatus[],
  onToggle: (status: ArticleStatus) => void,
): void {
  if (status !== 'archived') {
    onToggle(status);
    return;
  }
  const turnOn = !selected.includes('archived');
  for (const each of OFF_THE_SITE) {
    if (selected.includes(each) !== turnOn) {
      onToggle(each);
    }
  }
}
